import {
  LoadingManager,
  TextureLoader,
  Texture,
  MeshPhysicalMaterial,
  LinearFilter,
  DoubleSide,
  VideoTexture,
  CanvasTexture
} from "three";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

class Assets {
  manager = new LoadingManager();
  textureLoader = new TextureLoader(this.manager);
  gltfLoader = new GLTFLoader(this.manager);
  path;
  logo = null;
  logoPng = null;
  menuTransition = []
  menuImages = []
  menuVideos = []
  defaultTexture = null;
  // Menu media (project loops shown in the 3D menu) are heavy: ~45 MB of
  // GIFs plus eight videos. The texture objects are created at once so the
  // menu can be built, but the files are only fetched after the page has
  // loaded and the browser is idle, or when the menu is opened, whichever
  // comes first (client feedback round 2, item 6).
  pendingImages = []   // small menu frames: fetched at low priority once idle
  pendingLoops = []    // project loops (GIFs, several MB each): fetched on menu intent
  pendingVideos = []   // project videos: fetched on menu intent
  menuFramesLoaded = false
  onMenuTextureReady = []  // (index, texture) callbacks, see menu/ring.js
  touchDevice = window.matchMedia("(hover: none), (pointer: coarse)").matches
  menuMediaLoaded = false

  constructor() {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#010101'; 
    ctx.fillRect(0, 0, 1, 1);

    this.defaultTexture = new CanvasTexture(canvas);
    this.defaultTexture.minFilter = LinearFilter;
  }
  initialize() {
    const menuImages = document.body.querySelectorAll(".cinetic-creative-core-menu .wp-block-cinetic-creatives-blocks-adjacent-posts img");
    menuImages.forEach((image) => {
        const texture = new Texture()
        texture.minFilter = LinearFilter
        this.menuImages.push(texture)
        this.pendingLoops.push({ texture, src: image.currentSrc || image.getAttribute("src") })
    })

    const menuVideos = document.body.querySelectorAll(".cinetic-creative-core-menu .wp-block-cinetic-creatives-blocks-adjacent-posts article");
    menuVideos.forEach((item, i) => {
        const categories = item.dataset.categories.split(" ");
        // The project photo shows first; the loop video replaces it once it can
        // play. Videos are only fetched when the menu is reached for, so a
        // plane that started on the video texture stayed black for a while
        // on phones (client, 2026-10-08).
        let texture = this.menuImages[i] || this.defaultTexture;
        texture.categories = categories

        // Phones and tablets keep the photos: eight 1080p loops decoding at
        // once froze the menu on an iPhone (Chrome, 2026-10-08), and the ring
        // shows one plane at a time there anyway.
        const video = this.touchDevice ? null : item.querySelector("video")
        if (video) {
            const videoTexture = new VideoTexture(video)
            videoTexture.categories = categories
            video.addEventListener('loadeddata', () => {
                videoTexture.needsUpdate = true;
                video.play()
                this.menuVideos[i] = videoTexture
                this.onMenuTextureReady.forEach((fn) => fn(i, videoTexture))
            });
            this.pendingVideos.push(video)
        }

        this.menuVideos.push(texture)
    })

    this.logoPng = this.textureLoader.load(`/wp-content/themes/cinetic/assets/models/logo.png`)
    this.perlin = this.textureLoader.load(`/wp-content/themes/cinetic/assets/models/perlin.png`)
    this.glitch = this.textureLoader.load(`/wp-content/themes/cinetic/assets/models/glitche.jpeg`)

    // The 29 menu transition frames (~3 MB) are fetched with the other menu
    // media, after the page is idle or when the menu opens. The ?v= tag makes
    // browsers drop the cached frames when the sequence changes (2026-10-08:
    // frames from the client's Outro CC clip, without the white flash).
    for (let i = 0; i <= 28; i++) {
        const texture = new Texture()
        this.menuTransition[i] = texture
        this.pendingImages.push({ texture, src: `/wp-content/themes/cinetic/assets/models/menu/Transition--export_${i}.jpg?v=20261008` })
    }

    const modelPath = '/wp-content/themes/cinetic/assets/models/logo.glb';
    this.gltfLoader.load(
        modelPath,
        (gltf) => {
            this.logo = gltf.scene;
        
            this.logo.scale.set(5, 5, 5);
            
            const material = new MeshPhysicalMaterial( {
                side: DoubleSide,
                transmission: 1.0,
                thickness: 0.01,
                metalness: 0.0,
                roughness: 0.0,
            } )

            this.logo.traverse((children) => {
                if (children.isMesh) {
                    children.position.z =  -0.0018215351738035679
                    children.material = material
                }
            })
            console.log("Model loaded successfully!", gltf);
        },
        (xhr) => {
            console.log((xhr.loaded / xhr.total * 100) + '% loaded');
        },
        (error) => {
            console.error('An error happened loading the GLB:', error);
        }
    );
  }
}

function loadTextureImage(entry, priority) {
    const img = new Image()
    img.decoding = "async"
    if ("fetchPriority" in img) img.fetchPriority = priority
    img.onload = () => {
        entry.texture.image = img
        entry.texture.needsUpdate = true
    }
    img.src = entry.src
}

// Small menu frames (~4 MB): once the page is idle, at low priority so page
// switches are never queued behind them.
Assets.prototype.loadMenuFrames = function () {
    if (this.menuFramesLoaded) return
    this.menuFramesLoaded = true
    this.pendingImages.forEach((entry) => loadTextureImage(entry, "low"))
    this.pendingImages = []
}

// Project loops and videos (tens of MB): only when the visitor reaches for
// the menu (hover, touch, focus) or opens it.
Assets.prototype.loadMenuMedia = function () {
    this.loadMenuFrames()
    if (this.menuMediaLoaded) return
    this.menuMediaLoaded = true

    this.loadMenuPhotos()

    this.pendingVideos.forEach((video) => {
        video.preload = "auto"
        video.load()
        video.play().catch(() => {})
    })
    this.pendingVideos = []
}

// Runs fn once the page has loaded and the browser is idle (at most 4 s after load).
Assets.prototype.whenIdle = function (fn) {
    const run = () => {
        if ("requestIdleCallback" in window) {
            window.requestIdleCallback(fn, { timeout: 4000 })
        } else {
            window.setTimeout(fn, 1500)
        }
    }
    if (document.readyState === "complete") {
        run()
    } else {
        window.addEventListener("load", run, { once: true })
    }
}

Assets.prototype.scheduleMenuMedia = function () {
    this.whenIdle(() => {
        this.loadMenuFrames()
        this.loadMenuPhotos()
    })
}

// Project photos of the menu ring (~100 KB each): once idle, so the ring is
// never empty while its loop videos are still arriving (client, 2026-10-08).
Assets.prototype.loadMenuPhotos = function () {
    this.pendingLoops.forEach((entry) => loadTextureImage(entry, "low"))
    this.pendingLoops = []
}

export default new Assets();
